﻿import type { Maybe } from '@/types'
import { msalInstance, tokenRequest, getEffectiveOrigin, handleRedirectPromiseCached } from '@/utils/msal'
import type {
    AccountInfo,
    NavigationClient,
    RedirectRequest
} from '@azure/msal-browser'
import { defineStore } from 'pinia'
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { useErrorStore } from './errorStore'
import { isMsalTransientAuthError } from '~shared/errors/sentryNoise'

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

export type UseAuthenticationStore = {
    account: Ref<Maybe<AccountInfo>>
    login: (request?: RedirectRequest) => Promise<void>
    startLogin: (request?: RedirectRequest) => Promise<void>
    logout: () => Promise<void>
    getToken: () => Promise<string | null>
    initialize: (client?: NavigationClient) => Promise<void>
    clearNewUser: () => void
    isNewUser: ComputedRef<boolean>
    isLoggedIn: ComputedRef<boolean>
    userId: ComputedRef<Maybe<string>>
    userFirstName: ComputedRef<string>
    userLastname: ComputedRef<string>
    userEmail: ComputedRef<Maybe<string>>
}
export const useAuthenticationStore = defineStore(
    'authentication',
    function (): UseAuthenticationStore {
        const account = ref<Maybe<AccountInfo>>(null)
        const initialized = ref(false)
        let initializationPromise: Promise<void> | null = null

        async function handleRedirect(): Promise<void> {
            try {
                // Detect redirect loop: if we have auth params in URL but already handling redirect
                const urlParams = new URLSearchParams(window.location.hash.substring(1))
                const hasAuthParams = urlParams.has('state') || urlParams.has('code')
                
                if (hasAuthParams) {
                    console.log('🔐 MSAL: Processing redirect with auth parameters...')
                    console.log('   State:', urlParams.get('state')?.substring(0, 50) + '...')
                }
                
                const result = await handleRedirectPromiseCached()
                
                if (result) {
                    console.log('✅ MSAL: Redirect handled successfully')
                    if (result.account) setAccount(result.account)
                    
                    // Clear hash from URL after successful redirect to prevent re-processing
                    if (window.location.hash) {
                        console.log('🧹 MSAL: Clearing hash after successful redirect')
                        window.history.replaceState(null, '', window.location.pathname + window.location.search)
                    }
                    
                    // Clear the loop counter on success
                    sessionStorage.removeItem('msal_redirect_loop_detected')
                    return
                }
                
                // If we had auth params but no result, something went wrong
                if (hasAuthParams) {
                    console.warn('⚠️ MSAL: Auth parameters present but handleRedirectPromise returned null')
                    console.warn('   This usually means a redirect URI mismatch or cross-environment pollution')
                    console.warn('   Clearing params and forcing clean state...')
                    
                    // Clear the hash
                    window.history.replaceState(null, '', window.location.pathname + window.location.search)
                    
                    // Force reload to break the loop
                    setTimeout(() => {
                        if (window.location.hash) {
                            console.log('🔄 MSAL: Hash still present after clear, forcing reload')
                            window.location.reload()
                        }
                    }, 100)
                }
            } catch (error: any) {
                console.error('❌ MSAL: Error handling redirect:', error)
                console.error('   Error code:', error.errorCode)
                console.error('   Error message:', error.errorMessage || error.message)
                
                // On error, clear the problematic hash to prevent infinite loop
                if (window.location.hash) {
                    console.log('🧹 MSAL: Clearing hash to prevent redirect loop')
                    window.history.replaceState(null, '', window.location.pathname + window.location.search)
                }
                
                // If it's a redirect URI mismatch or interaction error, clear MSAL cache
                if (error.errorCode === 'redirect_uri_mismatch' || 
                    error.errorCode === 'interaction_in_progress' ||
                    error.message?.includes('redirect')) {
                    console.warn('🧹 MSAL: Clearing MSAL cache due to redirect error')
                    
                    // Clear MSAL-specific localStorage keys
                    Object.keys(localStorage).forEach(key => {
                        if (key.startsWith('msal.') || key.includes('msal')) {
                            localStorage.removeItem(key)
                        }
                    })
                }
            }
        }
        async function initialize(client?: NavigationClient): Promise<void> {
            if (initialized.value) return
            if (initializationPromise) return initializationPromise

            initializationPromise = (async () => {
                await handleRedirect()
                if (client) {
                    msalInstance.setNavigationClient(client)
                }
                const accounts = msalInstance.getAllAccounts()
                if (accounts.length > 0) {
                    setAccount(accounts[0])
                }
                syncEventUserIdCookie()
                initialized.value = true
            })()

            return initializationPromise
        }
        function setAccount(newAccount: AccountInfo) {
            msalInstance.setActiveAccount(newAccount)
            // hack to get the user id from the id token
            if (newAccount.idTokenClaims?.idp === 'facebook.com') {

            }

            account.value = newAccount
        }

        function setUserId(userId: string) {
            var accountInfo = account.value
            if (accountInfo) {
                accountInfo.localAccountId = userId
                account.value = accountInfo
            }
        }

        async function login(
            request: RedirectRequest = {
                scopes: ['openid', import.meta.env.VITE_MSAL_SCOPE as string]
            }
        ): Promise<void> {
            try {
                var result = await msalInstance.loginRedirect(request)

                return result;
            } catch (error: any) {
                if (error.errorCode === 'interaction_in_progress') {
                    sessionStorage.clear()
                    return login(request)
                }
                if (isMsalTransientAuthError(error)) {
                    console.warn('[msal] login failed (transient):', error)
                    useErrorStore().sendError({
                        show: true,
                        message: 'Kunne ikke oprette forbindelse til login. Tjek din internetforbindelse og prøv igen.',
                    })
                    return
                }
                throw error
            }
        }

        async function startLogin(
            request: RedirectRequest = {
                scopes: ['openid', import.meta.env.VITE_MSAL_SCOPE as string]
            }
        ): Promise<void> {
            await initialize()
            await login(request)
        }
        async function getToken(): Promise<string | null> {
            syncEventUserIdCookie()
            try {
                return (await msalInstance.acquireTokenSilent(tokenRequest))
                    .accessToken
            } catch (error) {
                await msalInstance.acquireTokenRedirect(tokenRequest)
            }
            return null
        }
        async function logout() {
            try {
                await msalInstance.logoutRedirect({
                    postLogoutRedirectUri: getEffectiveOrigin()
                })
                account.value = null
            } catch (error: any) {
                if (error.errorCode === 'interaction_in_progress') {
                    console.warn('[msal] logout skipped — auth interaction already in progress')
                    return
                }
                throw error
            }
        }
        async function clearNewUser() {
            if (account.value?.idTokenClaims?.newUser === undefined) return
            account.value.idTokenClaims.newUser = false
        }
        const isNewUser = computed(
            () => (account.value?.idTokenClaims?.newUser as boolean) ?? false
        )
        const isLoggedIn = computed(() => {
            //    console.log(account.value)
            if (account.value) return true
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length > 0) {
                setAccount(accounts[0])
                return true
            }
            return false
        })

            const eventUserIdCookie = ref<string | null>(getCookie('eventUserId'))

            function syncEventUserIdCookie() {
                const nextEventUserId = getCookie('eventUserId')

                if (eventUserIdCookie.value !== nextEventUserId) {
                    eventUserIdCookie.value = nextEventUserId
                }

                if (nextEventUserId) {
                    setUserId(nextEventUserId)
                }
            }

        const userEmail = computed<Maybe<string>>(() => {
            const claims = account.value?.idTokenClaims
            const emails = claims?.emails as string[] | undefined
            if (emails?.[0]) {
                return emails[0]
            }
            if (typeof claims?.email === 'string' && claims.email) {
                return claims.email
            }
            if (typeof claims?.preferred_username === 'string' && claims.preferred_username.includes('@')) {
                return claims.preferred_username
            }
            return account.value?.username ?? null
        });

        // Computed property for userId
        const userId = computed<Maybe<string>>(() => {
            return eventUserIdCookie.value ?? account.value?.localAccountId ?? null;
        });

        const userFirstName = computed(
        
            () => (account.value?.idTokenClaims?.given_name as string) ?? ''
        )

        const userLastname = computed(
            () => (account.value?.idTokenClaims?.family_name as string) ?? ''
        )
        return {
            account,
            login,
            startLogin,
            logout,
            getToken,
            initialize,
            clearNewUser,
            isNewUser,
            isLoggedIn,
            userId,
            userFirstName,
            userLastname,
            userEmail
            
        }
    }
)
